import {
  MessagingProvisioningStatus,
  registrationNeedsAttention,
  type CommitteeFacts,
  type CommitteeFactsDto,
  type TenantCampaignWithCapabilitiesDto,
  type WorkOrderDto,
  WorkOrderStatus,
} from '@signalvote/shared';
import {
  REGISTRATION_WAIT_DAYS,
  getWorkOrderScheduleError,
  recipientsFor,
  savedRecipientChoice,
  textSendProblem,
} from '@/components/work-orders';
import { getCampaignNowTimeString, getCampaignTodayString } from '@/lib/calendarUtils';
import { SEND_STEP_ORDER, type SendStepKey } from './expressSteps';

/** What /express/send knows when it decides which steps to show and where to start. */
export interface ExpressFlowContext {
  campaign: TenantCampaignWithCapabilitiesDto;
  /** The campaign's newest unpaid Text send; null before one exists. */
  workOrder: WorkOrderDto | null;
  committee: CommitteeFactsDto;
}

/**
 * Whether a first send's date falls inside the carrier registration wait, so it may not have cleared
 * by then. Both are campaign-local "YYYY-MM-DD" strings; no date is not inside the wait. "About 2
 * days" is no upper bound, so a date 2 days out counts as inside.
 */
export function sendsInsideRegistrationWait(sendDate: string | null, today: string): boolean {
  if (!sendDate) return false;
  const days = (Date.parse(`${sendDate}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000;
  return days <= REGISTRATION_WAIT_DAYS;
}

/** The facts carriers can't register a committee without. */
export const REQUIRED_COMMITTEE_FACTS = ['legalName', 'ein', 'filingPhone', 'filingEmail'] as const satisfies readonly (keyof CommitteeFacts)[];

/** Past these, the operator has the facts and they can't change. */
const LOCKED: readonly MessagingProvisioningStatus[] = [
  MessagingProvisioningStatus.SubmittedToVendor,
  MessagingProvisioningStatus.Provisioned,
];

export function hasRequiredFacts(facts: CommitteeFacts): boolean {
  return REQUIRED_COMMITTEE_FACTS.every((key) => !!facts[key]?.trim());
}

interface StepRule {
  /** True when the step has nothing to ask this time. */
  skip?: (context: ExpressFlowContext) => boolean;
  /** True when the saved Draft and profile already answer the step; resume starts at the first that doesn't. */
  answered: (context: ExpressFlowContext) => boolean;
}

/**
 * Each send step's rules, read from what the server holds, never from a stored step: so resuming on
 * another device, or after the Draft changed elsewhere, starts at the right question.
 */
const STEP_RULES: Record<SendStepKey, StepRule> = {
  message: {
    answered: ({ workOrder }) =>
      !!workOrder &&
      textSendProblem({
        body: workOrder.details.messageText ?? '',
        paidForDisclaimer: workOrder.details.paidForDisclaimer ?? '',
      }) === null,
  },
  recipients: {
    answered: ({ workOrder }) => !!workOrder && !!recipientsFor(workOrder, savedRecipientChoice(workOrder)),
  },
  schedule: {
    answered: ({ workOrder }) =>
      !!workOrder?.sendDate &&
      !!workOrder.sendTime &&
      getWorkOrderScheduleError(workOrder.sendDate, workOrder.sendTime, getCampaignTodayString(), getCampaignNowTimeString()) === null,
  },
  registration: {
    // Asked on a first send, and again whenever carriers turned the details down.
    skip: ({ workOrder, committee }) =>
      LOCKED.includes(committee.status) ||
      (workOrder?.isFirstSend === false && !registrationNeedsAttention(committee.status)),
    answered: ({ committee }) => hasRequiredFacts(committee.facts) && !registrationNeedsAttention(committee.status),
  },
  review: { answered: () => false },
};

/**
 * The steps this send asks, in order. Sent to the committee step with no Draft to pay for, as a paid
 * send's Fix your details does, the person only fixes and signs the details.
 */
export function stepsFor(context: ExpressFlowContext, startAt?: SendStepKey): readonly SendStepKey[] {
  if (!context.workOrder && startAt === 'registration') return ['registration'];
  // Its invoice is raised, so nothing about it can change; all that's left is paying.
  if (context.workOrder?.status === WorkOrderStatus.Invoiced) return ['review'];
  return SEND_STEP_ORDER.filter((step) => !STEP_RULES[step].skip?.(context));
}

/** Where a returning person picks up: the first step the server doesn't already answer. */
export function firstUnansweredStep(steps: readonly SendStepKey[], context: ExpressFlowContext): SendStepKey {
  return steps.find((step) => !STEP_RULES[step].answered(context)) ?? steps[steps.length - 1];
}
